import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Alert,
  Snackbar,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { RootState } from '@/store/store';
import {
  fetchSchedulerConfigsRequested,
  createSchedulerConfigRequested,
  updateSchedulerConfigRequested,
  deleteSchedulerConfigRequested,
  fetchSchedulerLogsRequested,
  fetchSchedulerStatusRequested,
  manuallyTriggerSchedulerRequested,
  openCreateDialog,
  openEditDialog,
  openDeleteDialog,
  openLogsDialog,
  closeCreateDialog,
  closeEditDialog,
  closeDeleteDialog,
  closeLogsDialog,
  clearSchedulerError
} from '@/store/slices/smsSchedulerSlice';
import { SchedulerConfigFormData, SchedulerLogFilters } from '@/types/smsScheduler';

// Import components
import SchedulerConfigTable from '@/components/sms/scheduler/SchedulerConfigTable';
import SchedulerStatusCard from '@/components/sms/scheduler/SchedulerStatusCard';
import SchedulerCreateDialog from '@/components/sms/scheduler/SchedulerCreateDialog';
import SchedulerEditDialog from '@/components/sms/scheduler/SchedulerEditDialog';
import SchedulerDeleteDialog from '@/components/sms/scheduler/SchedulerDeleteDialog';
import SchedulerLogsDialog from '@/components/sms/scheduler/SchedulerLogsDialog';

const SmsScheduler: React.FC = () => {
  const dispatch = useDispatch();
  
  // Redux state
  const {
    configurations,
    configurationsLoading,
    configurationsError,
    currentConfig,
    configLoading,
    configError,
    logs,
    logsLoading,
    logsError,
    status,
    statusLoading,
    statusError,
    isCreateDialogOpen,
    isEditDialogOpen,
    isDeleteDialogOpen,
    isLogsDialogOpen,
    configToDelete
  } = useSelector((state: RootState) => state.smsScheduler);

  // Local state
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  // Load initial data
  useEffect(() => {
    dispatch(fetchSchedulerConfigsRequested());
    dispatch(fetchSchedulerStatusRequested());
  }, [dispatch]);

  // Auto-refresh status every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      dispatch(fetchSchedulerStatusRequested());
    }, 30000);

    return () => clearInterval(interval);
  }, [dispatch]);

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Show success/error messages
  const showMessage = (message: string, severity: 'success' | 'error' | 'info' = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  // Handle create configuration
  const handleCreateConfig = (data: SchedulerConfigFormData) => {
    dispatch(createSchedulerConfigRequested(data));
  };

  // Handle edit configuration
  const handleEditConfig = (data: SchedulerConfigFormData) => {
    if (currentConfig) {
      dispatch(updateSchedulerConfigRequested({
        id: currentConfig.id,
        config: data
      }));
    }
  };

  // Handle delete configuration
  const handleDeleteConfig = () => {
    if (configToDelete) {
      dispatch(deleteSchedulerConfigRequested(configToDelete.id));
    }
  };

  // Handle manual trigger
  const handleManualTrigger = (configId: number) => {
    dispatch(manuallyTriggerSchedulerRequested(configId));
  };

  // Handle refresh
  const handleRefresh = () => {
    dispatch(fetchSchedulerConfigsRequested());
    dispatch(fetchSchedulerStatusRequested());
    showMessage('Data refreshed successfully', 'success');
  };

  // Handle logs refresh
  const handleLogsRefresh = (filters: SchedulerLogFilters) => {
    dispatch(fetchSchedulerLogsRequested(filters));
  };

  // Handle dialog actions
  const handleOpenCreateDialog = () => {
    dispatch(openCreateDialog());
  };

  const handleOpenEditDialog = (config: any) => {
    dispatch(openEditDialog(config));
  };

  const handleOpenDeleteDialog = (config: any) => {
    dispatch(openDeleteDialog(config));
  };

  const handleOpenLogsDialog = () => {
    dispatch(openLogsDialog());
    // Load logs with default filters
    dispatch(fetchSchedulerLogsRequested({ limit: 100 }));
  };

  const handleCloseCreateDialog = () => {
    dispatch(closeCreateDialog());
  };

  const handleCloseEditDialog = () => {
    dispatch(closeEditDialog());
  };

  const handleCloseDeleteDialog = () => {
    dispatch(closeDeleteDialog());
  };

  const handleCloseLogsDialog = () => {
    dispatch(closeLogsDialog());
  };

  // Clear errors when dialogs close
  const handleCloseDialogWithErrorClear = (closeAction: () => void) => {
    closeAction();
    dispatch(clearSchedulerError());
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            SMS Scheduler Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Configure and manage automated SMS notifications for policy renewals
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={configurationsLoading || statusLoading}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<HistoryIcon />}
            onClick={handleOpenLogsDialog}
          >
            View Logs
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
          >
            Create Scheduler
          </Button>
        </Box>
      </Box>

      {/* Error Alert */}
      {configurationsError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => dispatch(clearSchedulerError())}>
          {configurationsError}
        </Alert>
      )}

      {/* Status Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <SchedulerStatusCard
            status={status}
            loading={statusLoading}
            error={statusError}
          />
        </Grid>
      </Grid>

      <Divider sx={{ mb: 3 }} />

      {/* Configurations Section */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <ScheduleIcon color="primary" />
          <Typography variant="h5" component="h2">
            Scheduler Configurations
          </Typography>
        </Box>
        
        <SchedulerConfigTable
          configurations={configurations}
          loading={configurationsLoading}
          error={configurationsError}
          onEdit={handleOpenEditDialog}
          onDelete={handleOpenDeleteDialog}
          onTrigger={handleManualTrigger}
        />
      </Box>

      {/* Dialogs */}
      <SchedulerCreateDialog
        open={isCreateDialogOpen}
        onClose={() => handleCloseDialogWithErrorClear(handleCloseCreateDialog)}
        onSubmit={handleCreateConfig}
        loading={configLoading}
        error={configError}
      />

      <SchedulerEditDialog
        open={isEditDialogOpen}
        config={currentConfig}
        onClose={() => handleCloseDialogWithErrorClear(handleCloseEditDialog)}
        onSubmit={handleEditConfig}
        loading={configLoading}
        error={configError}
      />

      <SchedulerDeleteDialog
        open={isDeleteDialogOpen}
        config={configToDelete}
        onClose={() => handleCloseDialogWithErrorClear(handleCloseDeleteDialog)}
        onConfirm={handleDeleteConfig}
        loading={configLoading}
        error={configError}
      />

      <SchedulerLogsDialog
        open={isLogsDialogOpen}
        onClose={handleCloseLogsDialog}
        logs={logs}
        configurations={configurations}
        loading={logsLoading}
        error={logsError}
        onRefresh={handleLogsRefresh}
      />

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SmsScheduler;

